/**
 * MontaTech v2 - Profile API Service
 * 
 * Serviço para gerenciar perfis de configuração (API e localStorage)
 */

import { sendToTerminal } from '../../../../lib/terminalLogger';
import type { MontaTechProfile } from '../types';
import { STORAGE_KEYS, DEFAULT_PROFILE } from '../constants';

// ==================== MIGRATION ====================

/**
 * Migra perfis locais antigos: otstup era armazenado como meio-gap,
 * agora armazena gap total (valor que o usuario ve na UI).
 * Executa uma unica vez (flag no localStorage).
 */
function migrateOtstupIfNeeded(profiles: MontaTechProfile[]): MontaTechProfile[] {
  if (localStorage.getItem(STORAGE_KEYS.OTSTUP_MIGRATED)) return profiles;

  const migrated = profiles.map(p => ({
    ...p,
    otstup: (p.otstup && p.otstup > 0) ? p.otstup * 2 : p.otstup,
  }));

  saveLocalProfiles(migrated);
  localStorage.setItem(STORAGE_KEYS.OTSTUP_MIGRATED, '1');
  sendToTerminal('🔄 [MONTATECH] Migração otstup concluída: perfis locais atualizados para gap total', {
    count: migrated.length,
  });

  return migrated;
}

// ==================== LOCAL STORAGE ====================

/**
 * Carrega perfis salvos no localStorage
 */
export function loadLocalProfiles(): MontaTechProfile[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LOCAL_PROFILES);
    if (!stored) return [];

    let profiles = JSON.parse(stored);

    // Migração: otstup meio-gap → gap total (one-time)
    profiles = migrateOtstupIfNeeded(profiles);

    // Marca todos como locais
    return profiles.map((p: MontaTechProfile) => ({ ...p, isLocal: true }));
  } catch (error) {
    sendToTerminal('⚠️ [MONTATECH] Erro ao carregar perfis locais', { error: String(error) });
    return [];
  }
}

/**
 * Salva perfis no localStorage
 */
export function saveLocalProfiles(profiles: MontaTechProfile[]): boolean {
  try {
    // Remove flag isLocal antes de salvar (será re-adicionada ao carregar)
    const toSave = profiles.map(({ isLocal, ...rest }) => rest);
    localStorage.setItem(STORAGE_KEYS.LOCAL_PROFILES, JSON.stringify(toSave));
    return true;
  } catch (error) {
    sendToTerminal('❌ [MONTATECH] Erro ao salvar perfis locais', { error: String(error) });
    return false;
  }
}

/**
 * Adiciona um novo perfil local
 */
export function addLocalProfile(profile: MontaTechProfile): MontaTechProfile[] {
  const profiles = loadLocalProfiles();
  
  // Garante que o perfil é local
  const newProfile = { ...profile, isLocal: true };
  
  // Verifica se já existe um perfil com o mesmo nome
  const existingIndex = profiles.findIndex(p => p.name === newProfile.name);
  
  if (existingIndex >= 0) {
    // Atualiza o existente
    profiles[existingIndex] = newProfile;
  } else {
    // Adiciona novo
    profiles.push(newProfile);
  }
  
  saveLocalProfiles(profiles);
  return profiles;
}

/**
 * Remove um perfil local
 */
export function deleteLocalProfile(name: string): MontaTechProfile[] {
  const profiles = loadLocalProfiles();
  const filtered = profiles.filter(p => p.name !== name);
  
  if (filtered.length < profiles.length) {
    saveLocalProfiles(filtered);
  }
  
  return filtered;
}

// ==================== LAST PROFILE ====================

/**
 * Salva o último perfil selecionado
 */
export function saveLastProfileName(name: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_PROFILE, name);
  } catch {}
}

/**
 * Carrega o último perfil selecionado
 */
export function loadLastProfileName(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.LAST_PROFILE);
  } catch {
    return null;
  }
}

// ==================== PROFILE UTILS ====================

/**
 * Mescla perfis da API com perfis locais
 */
export function mergeProfiles(apiProfiles: MontaTechProfile[], localProfiles: MontaTechProfile[]): MontaTechProfile[] {
  // API profiles primeiro (marcados como não-local)
  const api = apiProfiles.map(p => ({ ...p, isLocal: false }));
  
  // Local profiles depois (já marcados como local)
  const local = localProfiles.map(p => ({ ...p, isLocal: true }));
  
  return [...api, ...local];
}

/**
 * Cria um novo perfil com valores padrão
 */
export function createDefaultProfile(name?: string): MontaTechProfile {
  return {
    ...DEFAULT_PROFILE,
    name: name || `Perfil ${Date.now()}`
  };
}

/**
 * Valida se um perfil tem todos os campos obrigatórios
 */
export function isValidProfile(profile: Partial<MontaTechProfile>): profile is MontaTechProfile {
  const required: (keyof MontaTechProfile)[] = [
    'name',
    'materialWith',
    'materialHeight',
    'gradusCount'
  ];
  
  return required.every(key => profile[key] !== undefined);
}

/**
 * Gera um nome único para novo perfil
 */
export function generateUniqueName(existingProfiles: MontaTechProfile[], baseName: string = 'Meu Perfil'): string {
  const existingNames = new Set(existingProfiles.map(p => p.name));
  
  if (!existingNames.has(baseName)) {
    return baseName;
  }
  
  let counter = 1;
  while (existingNames.has(`${baseName} ${counter}`)) {
    counter++;
  }
  
  return `${baseName} ${counter}`;
}

